import { useCallback, useEffect, useState } from "react";
import { Ban, FileCheck2, FileText, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { esCreditoArrendamiento, TIPOS_OT } from "@/lib/arrendamiento";
import { toast } from "sonner";
import { rutaOt } from "./OtShared";
import { OtEstadoBadge, OtEstadoDetalle } from "./OtEstado";
import { OtElegirDialog } from "./OtElegirDialog";
import { CancelarOpcionDialog, CompraDialog, MoraDialog } from "./OtDialogs";
import { ProrrogaDialog } from "./ProrrogaDialog";
import { DevolucionDialog } from "./DevolucionDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const verCarta = async (cid, folio = "") => {
  try {
    const res = await api.get(`/arrendamiento/creditos/${cid}/carta-transmision-pdf`, { params: folio ? { folio } : {}, responseType: "blob" });
    window.open(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
  } catch { toast.error("No se pudo abrir la carta de transmisión"); }
};

const verDocumento = async (docId) => {
  try {
    const { data } = await api.post(`/documentos/${docId}/signed-url`);
    window.open(`${BACKEND_URL}${data.url}`, "_blank");
  } catch { toast.error("No se pudo abrir el documento"); }
};

function Acciones({ ot, creditoId, onAbrir, onCancelar }) {
  const tipo = ot.opcion?.tipo;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {ot.visible && ot.puede_ejercer && (
        <Button size="sm" data-testid="ot-abrir-btn" onClick={onAbrir}><KeyRound className="mr-1.5 h-3.5 w-3.5" /> Opción terminal</Button>
      )}
      {tipo === "compra" && (
        <Button size="sm" variant="outline" data-testid="ot-carta-btn" onClick={() => verCarta(creditoId)}>
          <FileText className="mr-1.5 h-3.5 w-3.5" /> Carta de transmisión
        </Button>
      )}
      {tipo === "devolucion" && ot.opcion?.acta?.id && (
        <Button size="sm" variant="outline" data-testid="ot-acta-btn" onClick={() => verDocumento(ot.opcion.acta.id)}>
          <FileCheck2 className="mr-1.5 h-3.5 w-3.5" /> Acta de entrega
        </Button>
      )}
      {(tipo === "compra" || tipo === "devolucion") && ot.puede_cancelar && (
        <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="ot-cancelar-btn" onClick={onCancelar}>
          <Ban className="mr-1.5 h-3.5 w-3.5" /> Cancelar {TIPOS_OT[tipo].toLowerCase()}
        </Button>
      )}
    </div>
  );
}

// Build 3 · Bloque «Opción terminal» del arrendamiento en el detalle del crédito (Cartera).
export function OpcionTerminalBloque({ credito, onCambio }) {
  const [ot, setOt] = useState(null);
  const [dlg, setDlg] = useState(null);
  const cid = credito?.id;
  const esArr = esCreditoArrendamiento(credito);
  const firma = `${cid}|${credito?.estatus}|${credito?.saldo_insoluto}|${credito?.pagos_realizados}`;
  const cargar = useCallback(async () => {
    try { setOt((await api.get(rutaOt(cid))).data); } catch { setOt(null); }
  }, [cid]);
  useEffect(() => { if (esArr) cargar(); else setOt(null); }, [firma, esArr, cargar]);
  if (!ot || ot.estado === "rentas_pendientes" || (ot.estado === "sin_residual" && !ot.origen)) return null;
  const onDone = async () => { await cargar(); await onCambio?.(); };
  const props = { ot, creditoId: cid, onDone, onOpenChange: (v) => !v && setDlg(null) };
  return (
    <section className="mt-3 space-y-2 rounded-lg border border-slate-300 bg-white p-3 shadow-sm" data-testid="ot-bloque" data-estado={ot.estado}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex flex-wrap items-center gap-2 font-heading text-sm font-semibold text-slate-800">
          <KeyRound className="h-4 w-4 text-primary" /> Opción terminal <OtEstadoBadge ot={ot} />
        </h4>
        <Acciones ot={ot} creditoId={cid} onAbrir={() => setDlg("elegir")} onCancelar={() => setDlg("cancelar")} />
      </div>
      <OtEstadoDetalle ot={ot} onCarta={(folio) => verCarta(cid, folio)} />
      <OtElegirDialog open={dlg === "elegir"} onOpenChange={props.onOpenChange} ot={ot} onElegir={setDlg} />
      <MoraDialog open={dlg === "mora"} {...props} />
      <CompraDialog open={dlg === "compra"} {...props} />
      <ProrrogaDialog open={dlg === "prorroga"} {...props} />
      <DevolucionDialog open={dlg === "devolucion"} {...props} />
      {ot.opcion && <CancelarOpcionDialog open={dlg === "cancelar"} {...props} />}
    </section>
  );
}
